import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core";
import { Select } from "antd";
import { REPORT_FONT } from "../reportFonts";
import { sortFilterOptions, PRESERVE_ORDER_KEYS, toFilterSelectOptions, formatFilterMultiselectSummary, formatFilterMultiselectList, formatFilterDateLabel, FILTER_SELECT_SEARCH_PROPS, GlobalMultiSelectFilter, isAllFilterSelected } from "./filterRelationshipUtils";
import ActionMessages from "../../../common-components/ActionMessages";
import {
  getFilterYearsAPI,
  getFilterMonthsAPI,
  getFilterQuartersAPI,
  getFilterEmployeeIdsAPI,
  getIndirectDepartmentsAPI,
  getIndirectSubDepartmentsAPI,
  getIndirectEmployeeNamesAPI,
  getIndirectCostTypeFringeAPI,
  getIndirectFringeAccountsAPI,
} from "../../../api/reports-api";

const ACCENT = "#4F46E5";
const ACCENT2 = "#EEF2FF";
const BORDER = "#E5E7EB";
const MUTED = "#6B7280";

export { formatFilterDateLabel, formatFilterMultiselectList } from "./filterRelationshipUtils";

function buildSelectOptions(f, raw) {
  const list = PRESERVE_ORDER_KEYS.has(f.key) ? raw : sortFilterOptions(raw || [], f.key);
  return toFilterSelectOptions(f.key, list);
}

/* ══════════════════════════════════════════════════════════════ */
/*  Filter config (exported so parents can use helpers)          */
/* ══════════════════════════════════════════════════════════════ */
// apiFn/apiMap — only for filters NOT in filter_relationships (see useReportFilterOptions)
export const FILTERS_CONFIG = [
  {
    key: "year",
    label: "Year",
    type: "select",
    apiFn: getFilterYearsAPI,
    apiMap: (v) => String(v),
    defaultValue: String(new Date().getFullYear()),
  },
  {
    key: "contractType",
    label: "Contract Type",
    type: "multiselect",
    defaultValue: [],
  },
  {
    key: "contractId",
    label: "Contract ID",
    type: "multiselect",
    defaultValue: [],
  },
  {
    key: "clinLvl2",
    label: "CLIN 2nd Level",
    type: "multiselect",
    defaultValue: [],
  },
  {
    key: "account",
    label: "Account",
    type: "multiselect",
    apiFn: getIndirectFringeAccountsAPI,
    apiMap: (v) => v,
    defaultValue: [],
  },
  {
    key: "monthName",
    label: "Month",
    type: "multiselect",
    apiFn: getFilterMonthsAPI,
    apiMap: (v) => v,
    defaultValue: [],
  },
  {
    key: "quarter",
    label: "Quarter",
    type: "multiselect",
    apiFn: getFilterQuartersAPI,
    apiMap: (v) => v,
    defaultValue: [],
  },
  {
    key: "orgId",
    label: "Org_Id",
    type: "multiselect",
    defaultValue: [],
  },
  {
    key: "businessType",
    label: "Business Type",
    type: "multiselect",
    defaultValue: [],
  },
  {
    key: "awardType",
    label: "Award Type",
    type: "multiselect",
    defaultValue: [],
  },
  {
    key: "competitionType",
    label: "Competition Type",
    type: "multiselect",
    defaultValue: [],
  },
  {
    key: "bu",
    label: "BU",
    type: "multiselect",
    defaultValue: [],
  },
  {
    key: "popEndDate",
    label: "POP End Date",
    type: "daterange",
    defaultValue: { start: "", end: "" },
  },
  {
    key: "sector",
    label: "Sector",
    type: "multiselect",
    defaultValue: [],
  },
  {
    key: "projectId",
    label: "Project ID",
    type: "multiselect",
    defaultValue: [],
  },
  {
    key: "contractName",
    label: "Contract Name",
    type: "multiselect",
    defaultValue: [],
  },
  {
    key: "customerName",
    label: "Customer Name",
    type: "multiselect",
    defaultValue: [],
  },
  {
    key: "employeeId",
    label: "Employee ID",
    type: "multiselect",
    apiFn: getFilterEmployeeIdsAPI,
    apiMap: (v) => v.empl_id,
    defaultValue: [],
  },
  {
    key: "employeeType",
    label: "Employee Type",
    type: "multiselect",
    options: ["Employee", "Contractor"],
    defaultValue: [],
  },
  {
    key: "programManager",
    label: "Program Manager",
    type: "multiselect",
    defaultValue: [],
  },
  {
    key: "pc",
    label: "PC",
    type: "multiselect",
    defaultValue: [],
  },
  {
    key: "department",
    label: "Department",
    type: "multiselect",
    apiFn: getIndirectDepartmentsAPI,
    apiMap: (v) => v,
    defaultValue: [],
  },
  {
    key: "subDepartment",
    label: "Sub Department",
    type: "multiselect",
    apiFn: getIndirectSubDepartmentsAPI,
    apiMap: (v) => v,
    defaultValue: [],
  },
  {
    key: "employeeName",
    label: "Employee Name",
    type: "multiselect",
    apiFn: getIndirectEmployeeNamesAPI,
    apiMap: (v) => v,
    defaultValue: [],
  },
  {
    key: "costTypeIndirect",
    label: "Cost Type-Indirect",
    type: "multiselect",
    options: ["Indirect Labor", "Indirect Non Labor"],
    defaultValue: [],
  },
  {
    key: "costTypeFringe",
    label: "Cost Type - Fringe",
    type: "multiselect",
    apiFn: getIndirectCostTypeFringeAPI,
    apiMap: (v) => v,
    defaultValue: [],
  },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Exported helpers                                              */
/* ══════════════════════════════════════════════════════════════ */
export function initFilterValues() {
  return Object.fromEntries(
    FILTERS_CONFIG.map((f) => [
      f.key,
      Array.isArray(f.defaultValue)
        ? []
        : f.defaultValue !== null && typeof f.defaultValue === "object"
          ? { ...f.defaultValue }
          : f.defaultValue,
    ]),
  );
}

/** Coerce legacy popEndDate arrays / missing values to `{ start, end }`. */
export function normalizeFilterValues(values = {}) {
  const next = { ...values };
  FILTERS_CONFIG.forEach((f) => {
    if (f.type !== "daterange") return;
    const v = next[f.key];
    if (!v || Array.isArray(v) || typeof v !== "object") {
      next[f.key] = { start: "", end: "" };
    } else {
      next[f.key] = { start: v.start || "", end: v.end || "" };
    }
  });
  return next;
}

/** True when visible filter values match (array order does not matter). */
export function areVisibleFilterValuesEqual(a = {}, b = {}, visibleKeys) {
  const keys = visibleKeys?.length
    ? visibleKeys
    : FILTERS_CONFIG.map((f) => f.key);
  const norm = (v) => {
    if (Array.isArray(v)) return [...v].map(String).sort();
    if (v && typeof v === "object") {
      return { start: v.start || "", end: v.end || "" };
    }
    return v ?? null;
  };
  return (
    JSON.stringify(keys.map((k) => norm(a[k]))) ===
    JSON.stringify(keys.map((k) => norm(b[k])))
  );
}

export function isFilterActive(f, value) {
  if (f.type === "select") return value !== "" && value !== "All";
  if (f.type === "text") return value !== "";
  if (f.type === "multiselect") {
    if (isAllFilterSelected(value)) return false;
    return Array.isArray(value) && value.length > 0;
  }
  if (f.type === "daterange") {
    if (!value || Array.isArray(value) || typeof value !== "object") return false;
    return Boolean(value.start || value.end);
  }
  return false;
}

export function getDisplayValue(f, value) {
  if (f.type === "select") return !value || value === "All" ? "All" : value;
  if (f.type === "text") return !value ? "All" : value;
  if (f.type === "multiselect") {
    return formatFilterMultiselectSummary(f.key, value);
  }
  if (f.type === "daterange") {
    if (!value || typeof value !== "object" || Array.isArray(value)) return "All";
    if (!value.start && !value.end) return "All";
    const start = value.start ? formatFilterDateLabel(value.start) : "…";
    const end = value.end ? formatFilterDateLabel(value.end) : "…";
    return `${start} – ${end}`;
  }
  return "All";
}

export function getDefaultValue(f) {
  if (f.defaultValue !== undefined)
    return Array.isArray(f.defaultValue)
      ? []
      : typeof f.defaultValue === "object" && f.defaultValue !== null
        ? { ...f.defaultValue }
        : f.defaultValue;
  if (f.type === "select") return "All";
  if (f.type === "text") return "";
  if (f.type === "multiselect") return [];
  if (f.type === "daterange") return { start: "", end: "" };
  return "";
}

export function getActiveFilterCount(values, visibleKeys = null) {
  const cfg = visibleKeys
    ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key))
    : FILTERS_CONFIG;
  return cfg.filter((f) => isFilterActive(f, values[f.key])).length;
}

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                        */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    fontFamily: REPORT_FONT,
    background: "#fff",
    borderRadius: 12,
    border: `1px solid ${BORDER}`,
    boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    flexShrink: 0,
  },

  /* footer / apply */
  footer: {
    padding: "10px 12px",
    borderTop: `1px solid ${BORDER}`,
    background: "#FAFAFA",
    flexShrink: 0,
  },
  applyBtn: {
    width: "100%",
    height: 34,
    borderRadius: 7,
    border: "none",
    background: ACCENT,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: REPORT_FONT,
    letterSpacing: "0.01em",
    transition: "background 0.15s",
    "&:hover": { background: "#4338CA" },
    "&:active": { background: "#3730A3" },
  },

  /* header */
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderBottom: `1px solid ${BORDER}`,
    background: "#FAFAFA",
    flexShrink: 0,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 8 },
  headerIcon: { color: ACCENT, display: "flex", alignItems: "center" },
  headerTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#111827",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  clearAll: {
    fontSize: 11,
    fontWeight: 600,
    color: ACCENT,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "2px 6px",
    borderRadius: 4,
    "&:hover": { background: ACCENT2 },
  },

  /* body */
  body: { overflowY: "auto", flex: 1 },

  /* filter item */
  item: {
    borderBottom: `1px solid #F3F4F6`,
    "&:last-child": { borderBottom: "none" },
  },
  itemActive: { borderLeft: `3px solid ${ACCENT}`, background: "#FAFBFF" },
  itemInactive: { borderLeft: "3px solid transparent" },

  itemHeader: {
    padding: "10px 14px 10px 13px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    userSelect: "none",
    "&:hover": { background: "#F9FAFB" },
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    flexShrink: 0,
    marginTop: 1,
  },
  dotActive: { background: ACCENT },
  dotInactive: { background: "#D1D5DB" },

  itemMeta: { flex: 1, minWidth: 0 },
  itemLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  itemValue: {
    fontSize: 11,
    color: MUTED,
    marginTop: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  itemValueActive: { color: ACCENT, fontWeight: 500 },

  itemActions: { display: "flex", alignItems: "center", gap: 4, flexShrink: 0 },
  chevron: {
    color: "#9CA3AF",
    fontSize: 10,
    lineHeight: 1,
    transition: "transform 0.15s",
  },
  chevronOpen: { transform: "rotate(180deg)" },
  clearBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#9CA3AF",
    fontSize: 11,
    padding: 0,
    "&:hover": { background: "#FEE2E2", color: "#DC2626" },
  },

  /* expanded body */
  itemBody: {
    padding: "10px 14px 12px",
    background: "#F9FAFB",
    borderTop: `1px solid #F3F4F6`,
  },

  /* inputs */
  textInput: {
    width: "100%",
    height: 30,
    borderRadius: 6,
    border: `1.5px solid #D1D5DB`,
    fontSize: 12,
    color: "#111827",
    padding: "0 8px",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: REPORT_FONT,
    "&:focus": { borderColor: ACCENT, boxShadow: `0 0 0 2px ${ACCENT2}` },
  },
  dateRow: { display: "flex", flexDirection: "column", gap: 8 },
  datePair: { display: "flex", flexDirection: "column", gap: 3 },
  dateLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#9CA3AF",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  dateInput: {
    width: "100%",
    height: 30,
    borderRadius: 6,
    border: `1.5px solid #D1D5DB`,
    fontSize: 12,
    color: "#111827",
    padding: "0 8px",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: REPORT_FONT,
    "&:focus": { borderColor: ACCENT, boxShadow: `0 0 0 2px ${ACCENT2}` },
  },
  resetBtn: {
    marginTop: 6,
    fontSize: 11,
    color: ACCENT,
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    fontFamily: REPORT_FONT,
    "&:hover": { textDecoration: "underline" },
  },

  /* empty */
  empty: {
    padding: "24px 16px",
    textAlign: "center",
    fontSize: 12,
    color: MUTED,
  },
}));

/* ══════════════════════════════════════════════════════════════ */
/*  FilterItemBody                                                */
/* ══════════════════════════════════════════════════════════════ */
function FilterItemBody({ f, value, onChange, resolvedOptions }) {
  const opts = buildSelectOptions(f, resolvedOptions || f.options || []);
  const COMMON = {
    style: { width: "100%", fontFamily: REPORT_FONT },
    size: "small",
    dropdownStyle: { zIndex: 99999 },
    ...FILTER_SELECT_SEARCH_PROPS,
  };

  if (f.type === "select") {
    return (
      <Select
        {...COMMON}
        value={value || undefined}
        onChange={(val) => onChange(f.key, val)}
        options={opts}
        placeholder="Select…"
      />
    );
  }

  if (f.type === "text") {
    return (
      <input
        style={{
          width: "100%",
          height: 28,
          border: "1.5px solid #D1D5DB",
          borderRadius: 5,
          fontSize: 12,
          padding: "0 8px",
          outline: "none",
          fontFamily: REPORT_FONT,
          boxSizing: "border-box",
        }}
        placeholder="Enter value…"
        value={value}
        onChange={(e) => onChange(f.key, e.target.value)}
      />
    );
  }

  if (f.type === "multiselect") {
    return (
      <GlobalMultiSelectFilter
        {...COMMON}
        value={value}
        options={opts}
        onChange={(next) => onChange(f.key, next)}
        maxTagCount="responsive"
      />
    );
  }

  if (f.type === "daterange") {
    const range =
      value && typeof value === "object" && !Array.isArray(value)
        ? value
        : { start: "", end: "" };
    return (
      <div className={classes.dateRow}>
        <div className={classes.datePair}>
          <span className={classes.dateLabel}>From</span>
          <input
            type="date"
            className={classes.dateInput}
            value={range.start || ""}
            onChange={(e) =>
              onChange(f.key, { ...range, start: e.target.value })
            }
          />
        </div>
        <div className={classes.datePair}>
          <span className={classes.dateLabel}>To</span>
          <input
            type="date"
            className={classes.dateInput}
            value={range.end || ""}
            onChange={(e) => onChange(f.key, { ...range, end: e.target.value })}
          />
        </div>
        {(range.start || range.end) && (
          <button
            className={classes.resetBtn}
            onClick={() => onChange(f.key, { start: "", end: "" })}
          >
            Clear
          </button>
        )}
      </div>
    );
  }

  return null;
}

/**
 * Compact From–To date inputs for the horizontal filter bar.
 * Empty by default; does not prefill today's date.
 */
export function HorizontalDateRangeFilter({ value, onChange }) {
  const range =
    value && typeof value === "object" && !Array.isArray(value)
      ? value
      : { start: "", end: "" };

  const patch = (next) =>
    onChange({
      start: range.start || "",
      end: range.end || "",
      ...next,
    });

  const inputStyle = {
    height: 26,
    minWidth: 118,
    maxWidth: 136,
    flex: 1,
    boxSizing: "border-box",
    padding: "0 6px",
    border: "1px solid #D1D5DB",
    borderRadius: 4,
    fontSize: 12,
    fontFamily: REPORT_FONT,
    color: "#111827",
    background: "#fff",
    outline: "none",
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        minWidth: 250,
      }}
    >
      <input
        type="date"
        value={range.start || ""}
        title="From"
        aria-label="From"
        placeholder="From"
        onChange={(e) => patch({ start: e.target.value })}
        style={inputStyle}
      />
      <span style={{ fontSize: 11, color: "#94A3B8", flexShrink: 0 }}>–</span>
      <input
        type="date"
        value={range.end || ""}
        title="To"
        aria-label="To"
        placeholder="To"
        onChange={(e) => patch({ end: e.target.value })}
        style={inputStyle}
      />
    </div>
  );
}

/** Validate POP End Date range. Returns an error string, or null when valid / empty. */
export function getPopEndDateFilterError(popEndDate) {
  if (!popEndDate || Array.isArray(popEndDate) || typeof popEndDate !== "object") {
    return null;
  }
  const start = String(popEndDate.start || "").trim();
  const end = String(popEndDate.end || "").trim();
  if (!start && !end) return null;
  if (!start || !end) {
    return "Please select both a start date and an end date for POP End Date.";
  }
  if (start > end) {
    return "POP End Date start date cannot be greater than the end date.";
  }
  return null;
}

/**
 * Apply button that blocks apply when POP End Date is incomplete or inverted.
 * Shows ActionMessages on validation failure.
 */
export function FilterApplyButton({
  draft,
  onApply,
  disabled,
  className,
  children = "Apply",
  style,
}) {
  const [errorMsg, setErrorMsg] = useState(null);

  const handleClick = () => {
    if (disabled) return;
    const err = getPopEndDateFilterError(draft?.popEndDate);
    if (err) {
      setErrorMsg(err);
      return;
    }
    setErrorMsg(null);
    onApply?.();
  };

  return (
    <>
      <button
        type="button"
        className={className}
        style={style}
        disabled={disabled}
        onClick={handleClick}
      >
        {children}
      </button>
      {errorMsg && (
        <ActionMessages
          type="error"
          message={errorMsg}
          isClosed={() => setErrorMsg(null)}
        />
      )}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  GlobalFilterPanel                                             */
/*                                                                */
/*  When onApply is provided (Apply-button mode):                 */
/*    - Panel keeps an internal draft; edits stay local until     */
/*      the user clicks Apply, which calls onApply(draft).        */
/*    - values prop = the last-applied snapshot (used to sync     */
/*      the draft when the parent clears chips externally).       */
/*                                                                */
/*  When onApply is omitted (legacy uncontrolled mode):           */
/*    - Behaves as before; every change is immediate.             */
/* ══════════════════════════════════════════════════════════════ */
export default function GlobalFilterPanel({
  values: valuesProp,
  onApply,
  visibleFilters = null,
  onActiveCountChange,
  dropdownOptions = {} /* pre-loaded from parent on route entry */,
  fillHeight = false,
}) {
  const classes = useStyles();
  const hasApplyMode = typeof onApply === "function";

  /* Draft = what the user is currently editing in the panel.
     In Apply mode: starts from valuesProp (or defaults).
     In legacy mode: IS the committed state.              */
  const [draft, setDraft] = useState(() =>
    normalizeFilterValues(valuesProp ?? initFilterValues()),
  );

  /* Sync draft when parent commits new values (e.g. chip clear) */
  useEffect(() => {
    if (valuesProp !== undefined) setDraft(normalizeFilterValues(valuesProp));
  }, [valuesProp]);

  const [expanded, setExpanded] = useState({});

  const visibleCfg = visibleFilters
    ? FILTERS_CONFIG.filter((f) => visibleFilters.includes(f.key))
    : FILTERS_CONFIG;

  /* Badge count always reflects the committed (applied) values */
  const committedValues = valuesProp ?? draft;
  useEffect(() => {
    onActiveCountChange?.(
      getActiveFilterCount(committedValues, visibleFilters),
    );
  }, [committedValues, visibleFilters, onActiveCountChange]);

  const onDraftChange = (key, val) =>
    setDraft((prev) => ({ ...prev, [key]: val }));

  const toggleExpand = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const clearOne = (e, key, f) => {
    e.stopPropagation();
    onDraftChange(key, getDefaultValue(f));
  };

  const clearAll = () => {
    const reset = { ...draft };
    visibleCfg.forEach((f) => {
      reset[f.key] = getDefaultValue(f);
    });
    setDraft(reset);
  };

  const handleApply = () => onApply(draft);

  const hasDraftActive = visibleCfg.some((f) =>
    isFilterActive(f, draft[f.key]),
  );

  /* Dirty = draft differs from last-applied values */
  const isDirty =
    hasApplyMode &&
    !areVisibleFilterValuesEqual(draft, committedValues, visibleFilters);

  return (
    <div className={classes.root} style={fillHeight ? { flex: 1 } : {}}>
      {/* header */}
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <span className={classes.headerIcon}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="8" y1="12" x2="16" y2="12" />
              <line x1="10" y1="18" x2="14" y2="18" />
            </svg>
          </span>
          <span className={classes.headerTitle}>Filters</span>
        </div>
        {hasDraftActive && (
          <button className={classes.clearAll} onClick={clearAll}>
            Clear All
          </button>
        )}
      </div>

      {/* body */}
      <div className={classes.body}>
        {visibleCfg.length === 0 && (
          <div className={classes.empty}>
            No filters available for this view.
          </div>
        )}
        {visibleCfg.map((f) => {
          const value = draft[f.key];
          const active = isFilterActive(f, value);
          const isOpen = !!expanded[f.key];
          return (
            <div
              key={f.key}
              className={`${classes.item} ${active ? classes.itemActive : classes.itemInactive}`}
            >
              <div
                className={classes.itemHeader}
                onClick={() => toggleExpand(f.key)}
              >
                <span
                  className={`${classes.dot} ${active ? classes.dotActive : classes.dotInactive}`}
                />
                <div className={classes.itemMeta}>
                  <div className={classes.itemLabel}>{f.label}</div>
                  <div
                    className={`${classes.itemValue} ${active ? classes.itemValueActive : ""}`}
                  >
                    {active ? getDisplayValue(f, value) : "All"}
                  </div>
                </div>
                <div className={classes.itemActions}>
                  {active && (
                    <button
                      className={classes.clearBtn}
                      onClick={(e) => clearOne(e, f.key, f)}
                      title={`Clear ${f.label}`}
                    >
                      ✕
                    </button>
                  )}
                  <span
                    className={`${classes.chevron} ${isOpen ? classes.chevronOpen : ""}`}
                  >
                    ▼
                  </span>
                </div>
              </div>
              {isOpen && (
                <div className={classes.itemBody}>
                  <FilterItemBody
                    f={f}
                    value={value}
                    onChange={onDraftChange}
                    resolvedOptions={dropdownOptions[f.key] ?? f.options}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Apply button — only in Apply mode */}
      {hasApplyMode && (
        <div className={classes.footer}>
          <FilterApplyButton
            draft={draft}
            onApply={handleApply}
            className={classes.applyBtn}
            style={isDirty ? undefined : { opacity: 0.55, cursor: "default" }}
            disabled={!isDirty}
          >
            Apply Filters
          </FilterApplyButton>
        </div>
      )}
    </div>
  );
}
